import React, { useEffect, useRef } from "react";
import ReactPaginate from "react-paginate";
import {
  useFilters,
  useGlobalFilter,
  usePagination,
  useTable,
} from "react-table";
import { useSearchParams } from "react-router-dom";

// Define a default UI for global filtering
const GlobalFilter = ({ globalFilter, setGlobalFilter }) => {
  return (
    <input
      value={globalFilter || ""}
      onChange={(e) => setGlobalFilter(e.target.value)}
      placeholder="Search here..."
      className="border p-2 rounded-lg w-80 border-gray-300 outline-[#0D3D51]"
    />
  );
};

const Table = ({
  columns,
  data,
  screen,
  onAddPriceSurge,
  hideGlobalFilter = false,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = searchParams.get("page");
  const filterParam = searchParams.get("filter") || "";
  const initialPageIndex = pageParam ? parseInt(pageParam, 10) - 1 : 0; // Convert 1-based to 0-based
  const isFilterSyncingRef = useRef(false);
  const isPageSyncingRef = useRef(false);
  const lastPageParamRef = useRef(pageParam);
  const lastFilterParamRef = useRef(filterParam);
  const isUpdatingFilterUrlRef = useRef(false);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    setGlobalFilter,
    state: { pageIndex, globalFilter },
    pageCount,
    gotoPage,
  } = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: initialPageIndex >= 0 ? initialPageIndex : 0,
        globalFilter: filterParam,
      },
    },
    useFilters,
    useGlobalFilter,
    usePagination
  );

  // Sync filter from URL parameter (only when URL changes from navigation)
  useEffect(() => {
    // Skip if we're the ones updating the URL
    if (isUpdatingFilterUrlRef.current) {
      isUpdatingFilterUrlRef.current = false;
      return;
    }

    // Only sync if filterParam changed and is different from current filter
    if (filterParam !== lastFilterParamRef.current) {
      lastFilterParamRef.current = filterParam;
      if (filterParam !== globalFilter && !isFilterSyncingRef.current) {
        isFilterSyncingRef.current = true;
        setGlobalFilter(filterParam);
        setTimeout(() => {
          isFilterSyncingRef.current = false;
        }, 100);
      }
    }
  }, [filterParam, setGlobalFilter]); // Removed globalFilter from deps to prevent interference

  // Sync pageIndex with URL parameter (when URL changes from navigation)
  useEffect(() => {
    // Only sync if pageParam actually changed (not from our own update)
    if (pageParam === lastPageParamRef.current) return;
    lastPageParamRef.current = pageParam;

    const urlPage = pageParam ? parseInt(pageParam, 10) - 1 : 0;
    // Only sync if URL page is different and valid, and we're not already syncing
    if (
      !isPageSyncingRef.current &&
      urlPage !== pageIndex &&
      urlPage >= 0 &&
      (pageCount === 0 || urlPage < pageCount)
    ) {
      isPageSyncingRef.current = true;
      gotoPage(urlPage);
      setTimeout(() => {
        isPageSyncingRef.current = false;
      }, 100);
    }
  }, [pageParam, pageCount, pageIndex, gotoPage]);

  // Update URL when filter changes (debounced, reset to page 1 when filtering)
  useEffect(() => {
    // Skip if we're syncing from URL
    if (isFilterSyncingRef.current) return;

    const currentFilter = searchParams.get("filter") || "";

    // Only update if filter actually changed
    if (globalFilter === currentFilter) return;

    // Debounce URL updates - wait a bit after user stops typing
    const timeoutId = setTimeout(() => {
      // Mark that we're updating the URL so we don't sync back
      isUpdatingFilterUrlRef.current = true;

      const newSearchParams = new URLSearchParams(searchParams);

      // Update filter in URL
      if (globalFilter && globalFilter.trim() !== "") {
        newSearchParams.set("filter", globalFilter);
      } else {
        newSearchParams.delete("filter");
      }

      // Reset to page 1 when filter changes (user is actively filtering)
      if (
        globalFilter &&
        globalFilter.trim() !== "" &&
        currentFilter !== globalFilter
      ) {
        newSearchParams.delete("page"); // Reset to page 1
        lastPageParamRef.current = null; // Reset ref
        if (pageIndex !== 0) {
          isPageSyncingRef.current = true;
          gotoPage(0);
          setTimeout(() => {
            isPageSyncingRef.current = false;
          }, 100);
        }
      }

      setSearchParams(newSearchParams, { replace: true });
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timeoutId);
  }, [globalFilter, searchParams, setSearchParams, pageIndex, gotoPage]);

  // Update URL when page changes (when user clicks pagination)
  useEffect(() => {
    // Skip if we're syncing from URL or if pageCount is 0
    if (isPageSyncingRef.current || pageCount === 0) return;

    const newPage = pageIndex + 1; // Convert 0-based to 1-based for URL
    const currentPage = searchParams.get("page");

    // Only update URL if it's different (avoid unnecessary updates)
    if (currentPage !== String(newPage)) {
      isPageSyncingRef.current = true;
      const newSearchParams = new URLSearchParams(searchParams);
      if (newPage === 1) {
        newSearchParams.delete("page"); // Remove page param if it's page 1
        lastPageParamRef.current = null;
      } else {
        newSearchParams.set("page", String(newPage));
        lastPageParamRef.current = String(newPage);
      }
      setSearchParams(newSearchParams, { replace: true });
      // Reset flag after URL update
      setTimeout(() => {
        isPageSyncingRef.current = false;
      }, 0);
    }
  }, [pageIndex, pageCount, searchParams, setSearchParams]);

  return (
    <div className="pb-5">
      <div className="flex justify-between mb-4">
        {!hideGlobalFilter && (
          <GlobalFilter
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
          />
        )}
        {screen === "priceSurge" && (
          <button
            className="text-primary bg-celadon p-2 px-5 rounded-lg text-base"
            onClick={onAddPriceSurge}
          >
            Add Price Surge
          </button>
        )}
      </div>

      <div className="w-full overflow-x-auto rounded-sm shadow-sm custom-scrollbar">
        <table
          {...getTableProps()}
          className="min-w-full border border-gray-300 rounded-sm shadow-sm custom-scrollbar"
        >
          <thead className="bg-gray-100">
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps()}
                    className="px-4 py-3 border-b border-gray-300 text-left text-sm font-semibold text-dazzledBlue uppercase tracking-wider whitespace-nowrap overflow-hidden text-ellipsis"
                  >
                    {column.render("Header")}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()} className="divide-y divide-gray-200">
            {page.map((row) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()} className="hover:bg-gray-50">
                  {row.cells.map((cell) => (
                    <td
                      {...cell.getCellProps()}
                      className="px-4 py-3 whitespace-nowrap text-sm text-gray-700"
                    >
                      {cell.render("Cell")}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex justify-center mt-4">
        <ReactPaginate
          previousLabel={
            <span className="px-3 py-1 text-gray-500 font-semibold">
              Previous
            </span>
          }
          nextLabel={
            <span className="px-3 py-1 text-dazzledBlue font-semibold">
              Next
            </span>
          }
          pageCount={pageCount}
          forcePage={pageIndex}
          onPageChange={({ selected }) => gotoPage(selected)}
          containerClassName="flex gap-2"
          pageClassName="px-3 py-1"
          previousClassName="border-none cursor-pointer mt-1"
          nextClassName="border-none cursor-pointer mt-1"
          activeClassName="border-none bg-primary text-white rounded-lg"
        />
      </div>
    </div>
  );
};

export default Table;

